<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { MATERIAL_TYPE_ICONS } from '../lib/icons';
  import { onMount } from 'svelte';
  import { replace } from 'svelte-spa-router';
  import TopBar from '../components/TopBar.svelte';
  import HomeBanner from '../components/HomeBanner.svelte';
  import { session, applySession } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { getInitData, isInsideTelegram } from '../lib/telegram';
  import { durationLabel } from '../lib/format';
  import type { EventListItemDTO, MaterialType, RecentItemDTO } from '../lib/types';

  const HOME_LIMIT = 10;
  const EVENTS_LIMIT = 3;
  let recent = $state<RecentItemDTO[] | null>(null);
  let hasMoreRecent = $state(false);
  let upcomingEvents = $state<EventListItemDTO[] | null>(null);

  const EV_MONTH = ['січ', 'лют', 'бер', 'квіт', 'трав', 'черв', 'лип', 'серп', 'вер', 'жовт', 'лист', 'груд'];
  function eventWhen(iso: string): string {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getDate()} ${EV_MONTH[d.getMonth()]}, ${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  // Track scroll position inside the Recent window to toggle edge fades.
  let recentWindow = $state<HTMLDivElement | undefined>(undefined);
  let fadeTop = $state(false); // hide fade when at the very top
  let fadeBottom = $state(false); // hide fade when at the very bottom

  function updateRecentFade(): void {
    if (!recentWindow) return;
    const el = recentWindow;
    const max = el.scrollHeight - el.clientHeight;
    fadeTop = el.scrollTop > 1;
    fadeBottom = el.scrollTop < max - 1;
  }

  // Re-check fade state once items render or layout changes.
  $effect(() => {
    if (recent && recentWindow) {
      requestAnimationFrame(updateRecentFade);
    }
  });

  onMount(async () => {
    try {
      if (isInsideTelegram()) applySession(await api.authMe(getInitData()));
      else applySession(await api.authSession());
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        replace(import.meta.env.DEV ? '/' : '/login');
        return;
      }
    }

    // Fetch HOME_LIMIT + 1 to know if there's more — show "Переглянути більше" link in that case.
    try {
      const { items } = await api.myRecent(HOME_LIMIT + 1);
      hasMoreRecent = items.length > HOME_LIMIT;
      recent = items.slice(0, HOME_LIMIT);
    } catch {
      recent = [];
    }

    try {
      const { items } = await api.eventsUpcoming();
      upcomingEvents = items.slice(0, EVENTS_LIMIT);
    } catch {
      upcomingEvents = [];
    }
  });
</script>

{#if $session}
  <div class="screen">
    <TopBar />
    <div class="scroll card-list">
      <HomeBanner session={$session} />

      <div class="gap">
        <a class="sec-title sec-title-link" href="#/recent">Останні переглянуті матеріали</a>
        {#if recent === null}
          <div class="placeholder">Завантажуємо…</div>
        {:else if recent.length === 0}
          <div class="recent-empty">
            <div class="recent-empty-text">Ви ще не переглядали жодних матеріалів.</div>
            <a class="recent-empty-link" href="#/catalog">Перейти до списку →</a>
          </div>
        {:else}
          <div
            class="recent-window"
            bind:this={recentWindow}
            onscroll={updateRecentFade}
            style:--fade-top="{fadeTop ? 24 : 0}px"
            style:--fade-bottom="{fadeBottom ? 24 : 0}px"
          >
            {#each recent as item (item.id)}
              <a class="material" href={`#/material/${item.id}`}>
                <div class="material-icon"><Icon name={MATERIAL_TYPE_ICONS[item.type]} /></div>
                <div class="material-text">
                  <div class="material-title">{item.title}</div>
                  <div class="material-meta">
                    <span class="material-cat"><Icon name={item.category.icon} size="xs" /> {item.category.name}</span>
                    {#if item.durationSec}
                      <span> · {durationLabel(item.durationSec)}</span>
                    {/if}
                  </div>
                  {#if item.completedAt}
                    <div class="material-progress-row done"><Icon name="check" size="xs" /> Переглянуто</div>
                  {:else if item.maxProgressPct > 0}
                    <div class="material-progress-row">
                      <span class="material-pct">{item.maxProgressPct}%</span>
                      <div class="material-progress">
                        <div
                          class="material-progress-bar"
                          style="width: {item.maxProgressPct}%"
                        ></div>
                      </div>
                    </div>
                  {/if}
                </div>
                <div class="material-chevron">›</div>
              </a>
            {/each}
            {#if hasMoreRecent}
              <a class="material material-more" href="#/recent">
                <div class="material-text">
                  <div class="material-more-text">Переглянути більше</div>
                </div>
                <div class="material-chevron">›</div>
              </a>
            {/if}
          </div>
        {/if}
      </div>

      <div class="gap">
        <a class="sec-title-row" href="#/schedule">
          <span class="sec-title">Найближчі події</span>
          <span class="sec-more">Усі →</span>
        </a>
        {#if upcomingEvents === null}
          <div class="placeholder">Завантажуємо…</div>
        {:else if upcomingEvents.length === 0}
          <div class="placeholder">Поки що подій немає</div>
        {:else}
          {#each upcomingEvents as ev (ev.id)}
            <a class="event-row" class:locked={ev.locked} href={`#/event/${ev.id}`}>
              <div class="event-icon"><Icon name="calendar-days" /></div>
              <div class="event-main">
                <div class="event-name">{ev.title}</div>
                <div class="event-when">{eventWhen(ev.startsAt)}{#if ev.durationMin} · {ev.durationMin} хв{/if}</div>
              </div>
              {#if ev.locked}<div class="event-lock" aria-label="Заблоковано"><Icon name="lock" label="Заблоковано" /></div>{:else}<span class="event-chevron">›</span>{/if}
            </a>
          {/each}
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .screen {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    background: var(--bg);
  }
  .scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px 18px 100px;
  }
  .scroll::-webkit-scrollbar {
    width: 3px;
  }
  .scroll::-webkit-scrollbar-thumb {
    background: var(--border2);
    border-radius: 2px;
  }
  .gap {
    margin-bottom: 20px;
  }
  .sec-title {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-bottom: 10px;
  }
  .sec-title-link {
    display: block;
    text-decoration: none;
  }
  .sec-title-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    text-decoration: none;
    margin-bottom: 10px;
  }
  .sec-title-row .sec-title { margin-bottom: 0; }
  .sec-more { font-size: 11px; font-weight: 600; color: var(--accent-text); white-space: nowrap; }
  .event-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    margin-bottom: 8px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    text-decoration: none;
  }
  .event-row.locked { opacity: 0.7; }
  .event-icon { font-size: 20px; flex-shrink: 0; }
  .event-row.locked .event-icon { filter: grayscale(0.7); }
  .event-main { flex: 1; min-width: 0; }
  .event-name {
    font-size: 14px; font-weight: 600; color: var(--text);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .event-when { font-size: 12px; color: var(--text-muted); margin-top: 3px; }
  .event-chevron { flex-shrink: 0; color: var(--text-muted); font-size: 20px; }
  .event-lock { flex-shrink: 0; font-size: 15px; }
  /* Fixed-height window — internal scroll, leaves room for Events section below.
     `--fade-top` / `--fade-bottom` are driven from JS (updateRecentFade) so the
     gradient only kicks in when there's actually hidden content above/below. */
  .recent-window {
    --fade-top: 0px;
    --fade-bottom: 24px;
    max-height: 260px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-right: 4px;
    scrollbar-width: thin;
    scrollbar-color: var(--border2) transparent;
    -webkit-mask-image: linear-gradient(
      to bottom,
      transparent 0,
      black var(--fade-top),
      black calc(100% - var(--fade-bottom)),
      transparent 100%
    );
    mask-image: linear-gradient(
      to bottom,
      transparent 0,
      black var(--fade-top),
      black calc(100% - var(--fade-bottom)),
      transparent 100%
    );
  }
  .recent-window::-webkit-scrollbar {
    width: 3px;
  }
  .recent-window::-webkit-scrollbar-thumb {
    background: var(--border2);
    border-radius: 2px;
  }
  .material {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: var(--bg2);
    border: 1px solid var(--border);
    border-radius: 12px;
    text-decoration: none;
    color: inherit;
    margin-bottom: 8px;
  }
  .material-chevron {
    color: var(--text-muted);
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
  }
  /* "More" pseudo-item — same shape as material cards but action-styled */
  .material-more {
    justify-content: space-between;
    border-style: dashed;
  }
  .material-more-text {
    font-size: 12px;
    font-weight: 600;
    color: var(--accent-text);
  }
  .material-icon {
    font-size: 20px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bg);
    border-radius: 8px;
    flex-shrink: 0;
  }
  .material-text {
    flex: 1;
    min-width: 0;
  }
  .material-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .material-meta {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 2px;
  }
  .material-cat {
    color: var(--accent-text);
    font-weight: 600;
  }
  .material-progress-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    font-size: 11px;
    font-weight: 600;
  }
  .material-progress-row.done {
    color: var(--status-active);
  }
  .material-pct {
    color: var(--accent-text);
    flex-shrink: 0;
  }
  .material-progress {
    flex: 1;
    max-width: 50%;
    height: 2px;
    background: var(--border);
    border-radius: 999px;
    overflow: hidden;
  }
  .material-progress-bar {
    height: 100%;
    background: var(--pu);
    transition: width 0.3s var(--ease);
  }

  .recent-empty {
    padding: 18px 14px;
    text-align: center;
    border: 1px dashed var(--border2);
    border-radius: 12px;
    background: var(--bg2);
  }
  .recent-empty-text {
    font-size: 12px;
    color: var(--text-muted);
    line-height: 1.6;
    margin-bottom: 6px;
  }
  .recent-empty-link {
    font-size: 12px;
    color: var(--accent-text);
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
  }
  .placeholder {
    padding: 14px;
    text-align: center;
    border: 1px dashed var(--border2);
    border-radius: 12px;
    background: var(--bg2);
    font-size: 13px;
    font-weight: 600;
    color: var(--text-muted);
  }
</style>
